import { useState } from "react";
import { Form, FormField, NegativeButton, PrimaryButton, SecondaryButton } from "common/Forms"
import { InRow, ResponsiveGap } from "common/Grid";
import { NegativeStatusLabel, PositiveStatusLabel, ProgressStatusLabel, WarningStatusLabel } from "common/Label";
import { TableRenderer } from "common/Table"
import { Link, useNavigate } from 'react-router-dom';
import { FaLocationArrow } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { ModalDialog } from "common/Dialog";
import { Grid, GridCell } from "common/Grid/Grid";
import { TextAreaInput } from "common/Forms/inputs";
import { useSiteDeclineApiMutation, useSiteGenerateApiMutation } from "store/action/inquiryApi";
import { toast } from "react-toastify";
import { LuPencilLine } from "react-icons/lu";
import * as paths from 'app-router/paths';
import { usePermissions } from 'context/PermissionsContext';
import { PermissionKeys } from 'Constant/Constant';

export const TableView = ({ list, searchAsync }) => {
  const { allowedKeys, role } = usePermissions();
  const [sitGenerateModalOpen, setSiteGenerateModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [siteDeclineApi] = useSiteDeclineApiMutation();
  const [siteGenerateApi] = useSiteGenerateApiMutation();
  const [userInput, setUserInput] = useState({});
  const navigate = useNavigate();

  const hasPermission = (permissionKey) => {
    return role === "Admin" || allowedKeys.includes(permissionKey);
  }

  const onModelCancelHandler = () => {
    setSiteGenerateModalOpen(false);
    setDeclineModalOpen(false);
  }

  const onSiteGenerateHandler = async () => {
    await siteGenerateApi(userInput.webClientid).then((res) => {
      if (res?.data?.status === 'SUCCESS') {
        toast.warning(res?.data?.message || 'Site generation in progress.');
        onModelCancelHandler();
        searchAsync();
        setUserInput({});
      } else {
        toast.error('Something went wrong, please try again.');
        onModelCancelHandler();
        searchAsync();
        setUserInput({});
      }
    });
  }

  const onDeclineHandler = async () => {
    await siteDeclineApi(userInput).then((res) => {
      if (res?.data?.status === 'SUCCESS') {
        toast.error(res?.data?.message || 'Site generation declined.');
        onModelCancelHandler();
        searchAsync();
        setUserInput({});
      } else {
        toast.error('Something went wrong, please try again.');
        onModelCancelHandler();
        searchAsync();
        setUserInput({});
      }
    });
  }

  const onSiteGeneratePopupOpenHanlder = (id) => {
    setSiteGenerateModalOpen(true);
    setUserInput({ ...userInput, webClientid: id });
  }

  const onSiteDeclinePopupOpenHanlder = (id) => {
    setDeclineModalOpen(true);
    setUserInput({ ...userInput, webClientid: id });
  }

  return (
    <Form>
      <TableRenderer
        data={list ?? []}
        columns={
          [
            (hasPermission(PermissionKeys.EDIT_INQUIRIES) || hasPermission(PermissionKeys.APPROVE_REJECT_INQUIRIES)) && {
              name: 'Actions',
              widthRequest: { min: 200 },
              bodyRenderer: x =>
                <InRow gap={1}>
                  {hasPermission(PermissionKeys.APPROVE_REJECT_INQUIRIES) && (
                    <>
                      <PrimaryButton disabled={x?.status !== 1} title="Approve Inquiry" isWide={true} action={() => onSiteGeneratePopupOpenHanlder(x.id)}><FaLocationArrow /></PrimaryButton>
                      <NegativeButton disabled={x?.status !== 1} title="Reject Inquiry" action={() => onSiteDeclinePopupOpenHanlder(x.id)} isWide={true}><IoClose /></NegativeButton>
                    </>
                  )}
                  {hasPermission(PermissionKeys.EDIT_INQUIRIES) && (
                    <SecondaryButton disabled={x?.status !== 1} title="Edit Inquiry" action={() => navigate(paths.buildEditInquiryPath(x.id ?? 0))}>
                      <LuPencilLine />
                    </SecondaryButton>
                  )}
                </InRow>
            },
            {
              name: 'Status',
              widthRequest: { min: 150 },
              bodyRenderer: x =>
                x?.status === 1
                  ? <ProgressStatusLabel title={'Pending'} />
                  : x?.status === 2
                    ? <WarningStatusLabel title={'In-progress'} />
                    : x?.status === 3
                      ? <PositiveStatusLabel title={'Approved'} />
                      : <NegativeStatusLabel title={'Declined'} />
            },
            {
              name: 'Reason',
              widthRequest: { min: 200 },
              bodyRenderer: x => x?.declinedReason ?? <div>-</div>,
            },
            {
              name: 'Logo',
              bodyRenderer: x => x?.logoUrl ? <Link to={x?.logoUrl} target="_blank" ><img src={x?.logoUrl} alt="Logo" /></Link> : '-'
            },
            {
              name: 'Site ID',
              bodyRenderer: x => x?.siteid ?? '-',
            },
            {
              name: 'Name',
              bodyRenderer: x => x?.name ?? '-',
            },
            {
              name: 'Surname',
              bodyRenderer: x => x?.surname ?? '-',
            },
            {
              name: 'Website',
              bodyRenderer: x =>
                x?.website ? <Link to={x?.website} target="_blank" >{x?.website}</Link> : '-'
            },
            {
              name: 'Business Email',
              bodyRenderer: x => x?.businessEmail
            },
            {
              name: 'Business Trading Location',
              widthRequest: { min: 200 },
              bodyRenderer: x => x?.businessTradingLocation ?? '-',
            },
            {
              name: 'Business Mobile Number',
              bodyRenderer: x => x?.businessMobileNumber ?? '-',
            },
            {
              name: 'Business Category',
              bodyRenderer: x => x?.businessCategory ?? '-',
            },
            {
              name: 'Business Sub Category',
              widthRequest: { min: 150 },
              bodyRenderer: x => x?.businessSubcategory ?? '-',
            },
            {
              name: 'Business Trading Name',
              widthRequest: { min: 150 },
              bodyRenderer: x => x?.businessTradingName ?? '-',
            },
            {
              name: 'Business Type',
              bodyRenderer: x => x?.businessType ?? '-',
            },
            {
              name: 'Package',
              bodyRenderer: x => x?.packageSelected
            },
            {
              name: 'BankName',
              bodyRenderer: x => `${x?.bankName}`
            },
            {
              name: 'Account Number',
              bodyRenderer: x => x?.accountNumber ?? "-"
            },
            {
              name: 'Instagram',
              bodyRenderer: x => x?.instagramProfile ? <Link to={x?.instagramProfile} target="_blank" >{x?.instagramProfile}</Link> : '-',
            },
            {
              name: 'Facebook',
              bodyRenderer: x => x?.facebookProfile ? <Link to={x?.facebookProfile} target="_blank" >{x?.facebookProfile}</Link> : '-',
            },
            {
              name: 'Main Color',
              bodyRenderer: x => x?.mainColor ?? '-',
            },
            {
              name: 'Secondary Color',
              bodyRenderer: x => x?.secondaryColor ?? '-',
            },
          ].filter(Boolean)
        }
      />
      <ModalDialog isOpened={sitGenerateModalOpen} title='Confirm Site Generation'>
        <div className="p-5">
          This action will generate both the Customer App and the Staff App.
        </div>
        <ResponsiveGap left={false} right={false}>
          <Grid columns={2}>
            <GridCell>
              <PrimaryButton isWide={true} action={() => onSiteGenerateHandler()}>Generate Site</PrimaryButton>
            </GridCell>
            <GridCell>
              <SecondaryButton action={onModelCancelHandler} isWide={true}>Cancel</SecondaryButton>
            </GridCell>
          </Grid>
        </ResponsiveGap>
      </ModalDialog>

      <ModalDialog isOpened={declineModalOpen} title='Confirm Site Generation Decline'>
        <FormField
          id="reason"
          name="reason"
          label="Reason for decline"
          inputFactory={(id, name, onValidation) =>
            <TextAreaInput
              id={id} name={name}
              value={userInput.reason}
              placeholder='Enter reason'
              onValidation={onValidation}
              onChange={(_, n) => setUserInput({ ...userInput, reason: n })}
            />
          }
        />
        <ResponsiveGap left={false} right={false}>
          <Grid columns={2}>
            <GridCell>
              <NegativeButton classNames={['modal-negative-color']} isWide={true} action={() => onDeclineHandler()}>Decline</NegativeButton>
            </GridCell>
            <GridCell>
              <SecondaryButton action={onModelCancelHandler} isWide={true}>Cancel</SecondaryButton>
            </GridCell>
          </Grid>
        </ResponsiveGap>
      </ModalDialog>
    </Form>

  )
}